import React, { useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link, useSearchParams } from 'react-router-dom';
import NorthstarHistory from '../../components/NorthstarHistory';

export default function NorthstarPilot() {
  const { getToken } = useAuth();
  const [params] = useSearchParams();
  const [report, setReport] = useState(null);
  const [subject, setSubject] = useState(params.get('serviceTag') || '');
  const [historySubject, setHistorySubject] = useState(params.get('serviceTag') || '');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [revision, setRevision] = useState(0);
  const [reading, setReading] = useState(false);
  const selection = useRef(0);
  async function choose(event) {
    const selected = ++selection.current;
    setReport(null); setError(''); setReceipt(null); setAcknowledged(false);
    const file = event.target.files?.[0];
    setReading(!!file);
    if (!file) return;
    try {
      if (file.size > 2 * 1024 * 1024) throw new Error('The report exceeds 2 MiB.');
      const parsed = JSON.parse(await file.text());
      if (!parsed || parsed.schemaVersion !== 'northstar-pilot/1'
        || typeof parsed.subjectServiceTag !== 'string' || !parsed.subjectServiceTag.trim() || parsed.subjectServiceTag.length > 120
        || !['simulation', 'hardware'].includes(parsed.mode) || !['passed', 'failed', 'aborted', 'error'].includes(parsed.outcome)
        || typeof parsed.clientAttemptId !== 'string' || !/^[0-9a-f-]{36}$/i.test(parsed.clientAttemptId)
        || typeof parsed.profile?.id !== 'string' || typeof parsed.profile?.revision !== 'string'
        || !Array.isArray(parsed.measurements)) {
        throw new Error('Choose a Northstar pilot report (northstar-report.json).');
      }
      if (selected === selection.current) { setReport(parsed); setAcknowledged(false); }
    } catch (err) { if (selected === selection.current) setError(err.message); }
    finally { if (selected === selection.current) setReading(false); }
  }
  async function upload() {
    if (!report || !acknowledged || busy || reading) return;
    setBusy(true); setError(''); setReceipt(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in before importing the report.');
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/quality/station-runs/northstar-pilot`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(report),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Import failed. The local report remains available for retry.');
      setReceipt(body); setSubject(report.subjectServiceTag); setHistorySubject(report.subjectServiceTag); setRevision(value => value + 1);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  return <div style={{ maxWidth: 1080, margin: '0 auto', padding: '1.5rem' }}>
    <h1>Northstar</h1>
    <p style={{ color: 'var(--ni-text-muted)' }}>Display calibration and ECDIS verification · GPV engineering pilot</p>
    <p><Link className="ni-button ni-button--primary" to={`/quality/northstar/browser${subject ? `?serviceTag=${encodeURIComponent(subject)}` : ''}`}>Start browser Test calibration</Link></p>
    <div className="ni-panel" style={{ padding: '1.25rem' }}>
      <h2 style={{ marginTop: 0 }}>Import a Test run</h2>
      <p>Choose the report saved by Northstar. Confirmations, measurements, tables and generated file hashes are retained against its service tag. Failed and cancelled attempts belong here too.</p>
      <label style={{ display: 'block', marginBottom: '1rem' }}>Product family
        <select className="ni-select" value="test" disabled style={{ display: 'block', marginTop: '.4rem' }}><option value="test">Test</option></select>
      </label>
      <input aria-label="Northstar report" type="file" accept=".json,application/json" onChange={choose} disabled={busy} />
      {reading && <p role="status">Reading report…</p>}
      {report && <div style={{ marginTop: '1rem' }}>
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '.4rem 1rem' }}>
          <dt>Subject service tag</dt><dd style={{ margin: 0 }}>{report.subjectServiceTag}</dd>
          <dt>Mode / outcome</dt><dd style={{ margin: 0 }}>{report.mode} / {report.outcome}</dd>
          <dt>Profile</dt><dd style={{ margin: 0 }}>{report.profile?.id} · {report.profile?.revision}</dd>
          <dt>Measurements</dt><dd style={{ margin: 0 }}>{report.measurements.length}</dd>
          <dt>Attempt</dt><dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{report.clientAttemptId}</dd>
        </dl>
        <label style={{ display: 'block', marginBottom: '1rem' }}><input type="checkbox" checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} /> I checked the subject tag. Record this as experimental Test data; this is not production or ECDIS approval.</label>
        <button className="ni-button ni-button--primary" disabled={busy || reading || !acknowledged} onClick={upload}>{busy ? 'Importing…' : 'Save Test record to service tag'}</button>
      </div>}
      {error && <p role="alert">{error}</p>}
      {receipt && <p role="status">{receipt.replayed ? 'This exact attempt was already recorded. No duplicate created.' : 'Test record saved.'} Receipt: {receipt.run.id}</p>}
    </div>
    <form onSubmit={event => { event.preventDefault(); setHistorySubject(subject.trim()); setRevision(value => value + 1); }} style={{ display: 'flex', gap: '.75rem', marginTop: '1.5rem' }}>
      <input className="ni-input" aria-label="Service tag history" placeholder="Subject service tag" value={subject} onChange={event => setSubject(event.target.value)} />
      <button className="ni-button" type="submit">Find Test history</button>
    </form>
    <NorthstarHistory key={`${historySubject}-${revision}`} serviceTag={historySubject} />
    <p><Link to="/quality">Back to Product QMS</Link></p>
  </div>;
}
